import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

//icon
import SaveIcon from "@material-ui/icons/Save";

// jaza soft
import {
  PageHeader,
  WithReduxForm,
  BackButton,
  PageFooter,
  Button,
  TextInput,
  SelectInput,
  showNotification,
  required as jsRequired,
} from "jazasoft";
import { required } from "@jazasoft/mui-table";

// helper
import useMutation from "../../../hooks/useMutation";
import Forbidden from "../../../components/Forbidden";
import hasPrivilege from "../../../utils/hasPrivilege";

import { ResourceId, Scope } from "../../../utils/privilege";
import MuiTable from "../../../components/MuiTable";
import CardHeader from "../../../components/CardHeader";
import useDeepMemo from "../../../hooks/useDeepMemo";

import { isEmpty } from "../../../utils/helpers";
import { choicesApiOperation, choicesFileOperation, choicesHttpMethod, choicesFileType } from "./constants";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "1.5em",
  },
  mrTop: {
    marginTop: "1.5em",
  },
  pageFooter: {
    "& > button": {
      marginRight: theme.spacing(2),
    },
  },
}));

const ExternalAppCreate = props => {
  const classes = useStyles();
  const { i18nKey } = props;

  const formValues = useSelector(state => state.form && state.form["record-form"] && state.form["record-form"]?.values);
  const integrationType = formValues?.integrationType;

  // hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const [mutate, { loading: saving }] = useMutation();

  const onSubmit = (values, _, onSubmitComplete) => {
    const data = {
      ...formValues,
      integrationOperationList: values,
    };
    const options = {
      url: "externalApps",
      method: "post",
      data,
    };
    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("App registered successfully."));
        history.push(`/integration/externalApps/${data.id}/view`);
        onSubmitComplete && onSubmitComplete(values);
      },
    });
  };

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  const columns = useDeepMemo(() => {
    let columnList = [
      {
        dataKey: "entityName",
        title: "Entity Name",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "entityId",
        title: "Entity Id",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "entitySerial",
        title: "Entity Serial",
        inputType: "text-input",
        options: { type: "number" },
      },
      {
        dataKey: "operation",
        title: "Operation",
        inputType: "select-input",
        choices: integrationType === "API" ? choicesApiOperation : choicesFileOperation,
        validate: [required()],
      },
      {
        dataKey: "schedule",
        title: "Schedule",
        inputType: "text-input",
      },
      {
        dataKey: "meta",
        title: "Metadata",
        inputType: "text-input",
      },
      {
        dataKey: "enabled",
        title: "Enabled",
        inputType: "boolean-input",
      },
      {
        dataKey: "url",
        title: "URL",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "method",
        title: "Http Method",
        inputType: "select-input",
        choices: choicesHttpMethod,
        validate: [required()],
      },
      {
        dataKey: "headers",
        title: "Http Headers",
        inputType: "text-input",
      },
      {
        dataKey: "params",
        title: "Http Params",
        inputType: "text-input",
      },
      {
        dataKey: "authConfig",
        title: "Auth Config",
        inputType: "text-input",
      },
      {
        dataKey: "fileType",
        title: "File Type",
        inputType: "select-input",
        choices: choicesFileType,
        validate: [required()],
      },
      {
        dataKey: "location",
        title: "File Location",
        inputType: "text-input",
        validate: [required()],
      },
      {
        dataKey: "regexp",
        title: "Regexp (for filtering file)",
        inputType: "text-input",
      },
    ];
    const fileColumns = ["fileType", "location", "regexp"];
    const apiColumns = ["authConfig", "url", "method", "headers", "params"];
    if (integrationType === "API") {
      columnList = columnList.filter(c => !fileColumns.includes(c.dataKey));
    } else {
      columnList = columnList.filter(c => !apiColumns.includes(c.dataKey));
    }
    return columnList;
  }, [integrationType]);

  if (!hasPrivilege(props.roles, props.hasAccess, ResourceId.EXTERNAL_APP, Scope.WRITE)) {
    return <Forbidden history={props.history} />;
  }

  return (
    <div className={classes.root}>
      <PageHeader title="resources.styles.page.create" titleArgs={{ _: "Create Style" }} />

      <WithReduxForm initialValues={{ integrationType: "API", enabled: true }}>
        {({ handleSubmit }) => (
          <div>
            <Card>
              <CardHeader title="Basic Details" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextInput source="name" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextInput source="appId" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <SelectInput
                      source="integrationType"
                      i18nKey={i18nKey}
                      validate={[jsRequired()]}
                      choices={[
                        { id: "API", name: "API" },
                        { id: "FILE", name: "FILE" },
                      ]}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextInput source="serviceClass" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextInput source="authServiceClass" i18nKey={i18nKey} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <TextInput source="authConfig" i18nKey={i18nKey} fullWidth />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card className={classes.mrTop}>
              <CardHeader title="Operations" />
              <Divider />

              <PerfectScrollbar>
                <MuiTable
                  columns={columns}
                  rows={[{ enabled: true }]}
                  component="div"
                  editable
                  editing
                  enableRowAddition
                  showEditableActions
                  rowAddCount={1}
                  onRowAdd={(rows, rowIdx) => (!isEmpty(rowIdx) ? rows[rowIdx] : rows[rows?.length - 1])}
                  handleSubmitRef={handleSubmitRef}
                  onSubmit={onSubmit}
                  fontSize={16}
                />
              </PerfectScrollbar>
            </Card>

            <PageFooter className={classes.pageFooter}>
              <Button
                label="Save"
                color="primary"
                variant="contained"
                disabled={saving}
                onClick={event => {
                  handleSubmit(() => {})();
                  ffHandleSubmit(event);
                }}>
                <SaveIcon />
                {saving && <CircularProgress size={20} thickness={2} />}
              </Button>
              <BackButton label="Back" variant="outlined" />
            </PageFooter>
          </div>
        )}
      </WithReduxForm>
    </div>
  );
};

export default ExternalAppCreate;
